import React, { useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage, faTrashAlt } from '@fortawesome/pro-regular-svg-icons';
import { Label } from '@buildoutinc/blueprint-react/ui/Label';

import { Button } from 'components/ui/button';
import ImageDropzone from 'components/image_dropzone';

interface UploadResponse {
  id: number;
  url: string;
  file_name: string;
}

interface Props {
  existingPhotoUrl: string | null;
}

// Replaces the legacy `profile_photo`/`photo_delete` file inputs in `users/_form`. The dropzone
// uploads to `/uploads` and we surface the resulting Upload id (plus the delete flag) as hidden
// inputs inside the surrounding native form, so the existing "Update User" button persists them.
export default function ProfilePhotoUpload({ existingPhotoUrl }: Props) {
  const [uploadId, setUploadId] = useState<number | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(existingPhotoUrl);
  const [deleted, setDeleted] = useState(false);

  function handleUpload(data: UploadResponse) {
    setUploadId(data.id);
    setPreviewUrl(data.url);
    setDeleted(false);
  }

  function handleRemove(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    setUploadId(null);
    setPreviewUrl(null);
    // Only flag a server-side delete when there was a persisted photo to remove.
    setDeleted(Boolean(existingPhotoUrl));
  }

  return (
    <div className="row">
      <div className="col-md-12 mb-3">
        <Label className="form-label">Profile Photo</Label>

        {previewUrl && (
          <div className="d-flex align-items-center gap-3 mb-3">
            <img alt="Profile" className="img-thumbnail" src={previewUrl} style={{ maxHeight: 120 }} />
            <Button icon={faTrashAlt} label="Remove" size="sm" variant="ghost" onClick={handleRemove} />
          </div>
        )}

        <ImageDropzone
          allowedMedia={['image/*']}
          disablePreview
          dropBox={
            <div className="p-3 text-center text-muted">
              <FontAwesomeIcon className="me-1" icon={faImage} />
              {previewUrl ? 'Drag a new photo, or click here to replace' : 'Drag a photo, or click here to upload'}
            </div>
          }
          dropZoneClassName="media-uploader"
          onUploadComplete={handleUpload}
        />

        {uploadId && <input name="user[profile_photo_upload_id]" type="hidden" value={uploadId} />}
        <input name="user[photo_delete]" type="hidden" value={deleted ? 'true' : 'false'} />
      </div>
    </div>
  );
}
